import { useEffect, useState } from 'react';

interface CountdownTime {
  hours: string;
  minutes: string;
  seconds: string;
  isExpired: boolean;
}

interface UsePromotionTimerServiceProps {
  timestamp: number;
  onExpire?: () => void;
}

const SECOND = 1000;
const MINUTE = SECOND * 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;

export const usePromotionTimerService = ({ timestamp, onExpire }: UsePromotionTimerServiceProps) => {
  const [time, setTime] = useState<number>(0);
  const [isClient, setIsClient] = useState(false);

  const calculateCountdown = (): CountdownTime => {
    const start = new Date().getTime();
    const end = new Date(timestamp).getTime();
    const diff = end - start;

    if (start > end) {
      onExpire?.();
      return {
        hours: '00',
        minutes: '00',
        seconds: '00',
        isExpired: true,
      };
    }

    const days = Math.floor(diff / DAY);
    const remainingTime = diff % DAY;

    // 1일 이상이면 "N일 남음" 형식 (나머지 시간/분/초가 있으면 +1일)
    if (days >= 1) {
      const displayDays = remainingTime > 0 ? days + 1 : days;
      return {
        hours: `${displayDays}일 남음`,
        minutes: '',
        seconds: '',
        isExpired: false,
      };
    }

    // 1일 미만이면 시:분:초 형식
    const hours = Math.floor(diff / HOUR);
    const minutes = Math.floor((diff % HOUR) / MINUTE);
    const seconds = Math.floor((diff % MINUTE) / SECOND);

    const hh = hours.toString().padStart(2, '0');
    const mm = minutes.toString().padStart(2, '0');
    const ss = seconds.toString().padStart(2, '0');

    return {
      hours: hh,
      minutes: mm,
      seconds: ss,
      isExpired: false,
    };
  };

  const getFormattedCountdown = (): string => {
    if (!isClient) {
      return '';
    }
    const countdown = calculateCountdown();

    // 1일 이상이면 "N일 남음" 텍스트만 반환
    if (countdown.minutes === '' && countdown.seconds === '') {
      return countdown.hours;
    }

    // 1일 미만이면 시:분:초 형식
    return `${countdown.hours}:${countdown.minutes}:${countdown.seconds}`;
  };

  const isTimerActive = (): boolean => {
    return timestamp > 0;
  };

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isTimerActive()) {
      return;
    }

    const interval = setInterval(() => {
      setTime((prev) => prev + 1);
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [time, timestamp, isClient]);

  return {
    getFormattedCountdown,
    calculateCountdown,
    isTimerActive,
    isClient,
  };
};
